import React, { useState } from 'react';
import { useShop } from '../../context/ShopContext';
import { formatINR } from '../../data/products';
import { 
  Package, 
  Search, 
  Plus, 
  ArrowDownLeft, 
  ArrowUpRight, 
  Repeat, 
  AlertTriangle, 
  Barcode, 
  Scale, 
  Printer, 
  Truck, 
  Building2, 
  CheckCircle2, 
  ShieldCheck, 
  X, 
  FileSpreadsheet,
  Download,
  Flame
} from 'lucide-react';

export default function AdminInventory() {
  const { products, masterMetalRates, addToast } = useShop();

  const [activeTab, setActiveTab] = useState('stock-ledger'); // 'stock-ledger', 'purchase-entry', 'branch-transfers', 'melting-damaged'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedBarcodeItem, setSelectedBarcodeItem] = useState(null);
  const [isStockInModalOpen, setIsStockInModalOpen] = useState(false);

  // Sample branches
  const branches = [
    { id: 'b1', name: 'Main Vault Showroom (M.I. Road, Jaipur)' },
    { id: 'b2', name: 'Bapu Bazar Branch (Old City, Jaipur)' },
    { id: 'b3', name: 'Central E-Commerce Safe Vault' }
  ];

  // Stock In / Purchase Entry form
  const [stockInForm, setStockInForm] = useState({
    supplierName: 'M.L. Karigar Works & Casting Hub',
    supplierGstin: '08AABCM1234F1Z9',
    category: 'Jewellery',
    metalType: 'Gold',
    purity: '22KT (916)',
    grossWeight: 25.4,
    netWeight: 24.8,
    quantity: 2,
    huid: 'AB1234',
    branch: 'b1',
    purchaseCost: 165000,
    hallmarkCertified: true
  });

  // Calculate vault valuation
  const totalGoldGrams = products
    .filter(p => p.metalType === 'Gold' || (!p.metalType && p.category !== 'kitchenware' && p.category !== 'silverware'))
    .reduce((acc, p) => acc + (Number(p.weight) || 12) * (p.inStock ? 3 : 0), 0);

  const totalSilverGrams = products
    .filter(p => p.metalType === 'Silver' || p.category === 'silverware')
    .reduce((acc, p) => acc + (Number(p.weight) || 80) * (p.inStock ? 5 : 0), 0);

  const goldRate = masterMetalRates?.gold?.[1]?.rate || 6765;
  const silverRate = masterMetalRates?.silver?.[0]?.rate || 89.5;
  const totalValuation = Math.round(totalGoldGrams * goldRate + totalSilverGrams * silverRate);

  const handleCreateStockIn = (e) => {
    e.preventDefault();
    setIsStockInModalOpen(false);
    addToast?.(`Stock In voucher recorded: ${stockInForm.grossWeight}g ${stockInForm.purity} Gold with HUID: ${stockInForm.huid}`, 'success');
  };

  const handlePrintBarcode = (item) => {
    setSelectedBarcodeItem(item);
  };

  return (
    <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
      
      {/* Top Banner */}
      <div style={{
        background: '#ffffff',
        borderRadius: '8px',
        padding: '1.25rem 1.6rem',
        border: '1px solid #e0e0e0',
        marginBottom: '1.5rem',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#2874f0', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '0.2rem' }}>
            <Package size={14} />
            <span>VAULT PHYSICAL AUDIT & STOCK LEDGER</span>
          </div>
          <h2 style={{ fontSize: '1.45rem', fontWeight: 800, color: '#212121', margin: 0 }}>
            Inventory, Suppliers & Hallmark HUIDs
          </h2>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem', color: '#616161' }}>
            Multi-branch stock tracking, Karigar purchase entries, damaged metal melting records & BIS barcode printing.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setIsStockInModalOpen(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.55rem 1rem',
              borderRadius: '4px',
              border: 'none',
              background: '#2874f0',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: '0.82rem',
              cursor: 'pointer'
            }}
          >
            <Plus size={15} />
            <span>Purchase Entry (Stock In)</span>
          </button>
        </div>
      </div>

      {/* Vault Valuation Overview Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'VAULT GOLD RESERVES', val: `${totalGoldGrams.toFixed(2)} g`, sub: `Valued @ ₹${goldRate}/g`, color: '#d97706' },
          { label: 'VAULT SILVER STOCK', val: `${(totalSilverGrams / 1000).toFixed(2)} kg`, sub: `Valued @ ₹${silverRate}/g`, color: '#475569' },
          { label: 'TOTAL VAULT VALUATION', val: formatINR(totalValuation), sub: 'Physical Bullion + Ornaments', color: '#15803d' },
          { label: 'LOW STOCK ORNAMENTS', val: products.filter(p => !p.inStock).length, sub: 'Needs casting replenishment', color: '#dc2626' }
        ].map(k => (
          <div key={k.label} style={{ background: '#ffffff', padding: '1.1rem 1.3rem', borderRadius: '8px', border: '1px solid #e0e0e0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 800, color: '#757575', letterSpacing: '0.5px' }}>{k.label}</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: k.color, margin: '0.3rem 0' }}>{k.val}</div>
            <div style={{ fontSize: '0.72rem', color: '#9e9e9e' }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Tabs Bar */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', borderBottom: '1px solid #e0e0e0', paddingBottom: '0.5rem' }}>
        {[
          { id: 'stock-ledger', label: 'SKU & Barcode Ledger', icon: <Barcode size={15} /> },
          { id: 'branch-transfers', label: 'Multi-Branch Transfers', icon: <Repeat size={15} /> },
          { id: 'suppliers', label: 'Karigar & Artisan Suppliers', icon: <Building2 size={15} /> },
          { id: 'melting-damaged', label: 'Damaged & Old Gold Melting', icon: <Flame size={15} /> }
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.45rem',
              padding: '0.55rem 0.9rem',
              borderRadius: '4px',
              border: 'none',
              background: activeTab === t.id ? '#e8f0fe' : 'transparent',
              color: activeTab === t.id ? '#2874f0' : '#424242',
              fontWeight: activeTab === t.id ? 700 : 500,
              fontSize: '0.82rem',
              cursor: 'pointer'
            }}
          >
            {t.icon}
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* Tab 1: SKU & Barcode Ledger */}
      {activeTab === 'stock-ledger' && (
        <div style={{ background: '#ffffff', borderRadius: '8px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
          <div style={{ padding: '0.9rem 1.2rem', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.8rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: '220px' }}>
              <Search size={15} color="#757575" />
              <input
                type="text"
                placeholder="Search SKU, Product title, or HUID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '100%', border: 'none', outline: 'none', fontSize: '0.85rem' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              {branches.map(b => (
                <button
                  key={b.id}
                  onClick={() => setSelectedBranch(selectedBranch === b.id ? 'all' : b.id)}
                  style={{
                    padding: '0.3rem 0.6rem',
                    fontSize: '0.72rem',
                    borderRadius: '3px',
                    border: '1px solid #d0d7de',
                    background: selectedBranch === b.id ? '#2874f0' : '#fff',
                    color: selectedBranch === b.id ? '#fff' : '#333',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  {b.name.split(' ')[0]}
                </button>
              ))}
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '0.74rem', textTransform: 'uppercase' }}>
                <th style={{ padding: '0.8rem 1rem' }}>Ornament / SKU</th>
                <th style={{ padding: '0.8rem 1rem' }}>Metal & Purity</th>
                <th style={{ padding: '0.8rem 1rem' }}>Gross Wt</th>
                <th style={{ padding: '0.8rem 1rem' }}>Net Wt</th>
                <th style={{ padding: '0.8rem 1rem' }}>BIS Hallmark / HUID</th>
                <th style={{ padding: '0.8rem 1rem' }}>Vault Stock</th>
                <th style={{ padding: '0.8rem 1rem', textAlign: 'right' }}>Label Print</th>
              </tr>
            </thead>
            <tbody>
              {products
                .filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || (p.sku || '').toLowerCase().includes(searchTerm.toLowerCase()))
                .slice(0, 15)
                .map((prod, idx) => {
                  const sku = prod.sku || `MLJ-${prod.category?.slice(0, 3).toUpperCase() || 'JWL'}-${100 + idx}`;
                  const huid = prod.huid || `ML${idx}89G`;
                  const wt = Number(prod.weight) || (prod.category === 'kitchenware' ? 850 : 12.5);
                  return (
                    <tr key={prod.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '0.8rem 1rem' }}>
                        <div style={{ fontWeight: 700, color: '#1e293b' }}>{prod.name}</div>
                        <div style={{ fontSize: '0.72rem', color: '#2874f0', fontWeight: 600, fontFamily: 'monospace' }}>SKU: {sku}</div>
                      </td>
                      <td style={{ padding: '0.8rem 1rem', color: '#475569', fontWeight: 600 }}>
                        {prod.metalType || 'Gold'} • {prod.purity || '22KT (916)'}
                      </td>
                      <td style={{ padding: '0.8rem 1rem', color: '#334155', fontWeight: 600 }}>
                        {wt.toFixed(2)}g
                      </td>
                      <td style={{ padding: '0.8rem 1rem', color: '#065f46', fontWeight: 800 }}>
                        {(wt * 0.96).toFixed(2)}g
                      </td>
                      <td style={{ padding: '0.8rem 1rem' }}>
                        <span style={{ background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', padding: '0.15rem 0.5rem', borderRadius: '3px', fontWeight: 700, fontSize: '0.7rem' }}>
                          HUID: {huid}
                        </span>
                      </td>
                      <td style={{ padding: '0.8rem 1rem' }}>
                        <span style={{
                          padding: '0.2rem 0.55rem',
                          borderRadius: '4px',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          background: prod.inStock ? '#dcfce7' : '#fee2e2',
                          color: prod.inStock ? '#15803d' : '#b91c1c'
                        }}>
                          {prod.inStock ? 'In Vault (3 pcs)' : 'Low Stock (0 pcs)'}
                        </span>
                      </td>
                      <td style={{ padding: '0.8rem 1rem', textAlign: 'right' }}>
                        <button
                          onClick={() => handlePrintBarcode({ ...prod, sku, huid, wt })}
                          style={{
                            background: '#f1f5f9',
                            border: '1px solid #cbd5e1',
                            color: '#334155',
                            padding: '0.35rem 0.65rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}
                        >
                          <Printer size={13} />
                          <span>Barcode</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {/* Tab 2: Multi-Branch Transfers */}
      {activeTab === 'branch-transfers' && (
        <div style={{ background: '#ffffff', borderRadius: '8px', padding: '1.4rem', border: '1px solid #e0e0e0' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 800, margin: '0 0 1rem', color: '#1e293b' }}>
            Inter-Branch Stock Transit & Consignment
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            {branches.map(b => (
              <div key={b.id} style={{ border: '1px solid #e2e8f0', padding: '1rem', borderRadius: '6px', background: '#f8fafc' }}>
                <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>LOCATION NODE</div>
                <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#1e293b', margin: '0.3rem 0' }}>{b.name}</div>
                <div style={{ fontSize: '0.78rem', color: '#15803d', fontWeight: 700 }}>● Active Vault Connection</div>
              </div>
            ))}
          </div>
          <div style={{ background: '#eff6ff', border: '1px dashed #3b82f6', padding: '1.2rem', borderRadius: '6px', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#1e40af', fontWeight: 600 }}>
              To transfer inventory between Showroom and Vault, select items from SKU Ledger and click "Initiate Transfer Challan".
            </p>
          </div>
        </div>
      )}

      {/* Tab 3: Suppliers & Karigars */}
      {activeTab === 'suppliers' && (
        <div style={{ background: '#ffffff', borderRadius: '8px', padding: '1.4rem', border: '1px solid #e0e0e0' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 800, margin: '0 0 1rem', color: '#1e293b' }}>
            Artisan & Casting Karigar Ledgers
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
            {[
              { name: 'M.L. Karigar Casting Studio, Jaipur', metalBalance: '48.5g Gold', status: 'Settled', contact: '+91 94140 11223' },
              { name: 'Bengal Polki & Filigree Master Workshop', metalBalance: '120.0g Gold', status: 'Work in Progress', contact: '+91 98300 44556' },
              { name: 'Surat Certified Diamond Cutters Ltd', metalBalance: '14.5 Ct Solitaire', status: 'Verified', contact: '+91 98250 77889' }
            ].map(s => (
              <div key={s.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.9rem 1.1rem', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                <div>
                  <div style={{ fontWeight: 800, color: '#1e293b' }}>{s.name}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Contact: {s.contact}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 800, color: '#d97706', fontSize: '0.85rem' }}>{s.metalBalance}</div>
                  <div style={{ fontSize: '0.72rem', color: '#15803d', fontWeight: 700 }}>{s.status}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab 4: Damaged & Melting */}
      {activeTab === 'melting-damaged' && (
        <div style={{ background: '#ffffff', borderRadius: '8px', padding: '1.4rem', border: '1px solid #e0e0e0' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 800, margin: '0 0 1rem', color: '#1e293b' }}>
            Scrap Jewellery & Melting Furnace Log
          </h3>
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', padding: '1rem', borderRadius: '6px', marginBottom: '1.2rem', display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
            <Flame size={24} color="#d97706" />
            <div>
              <div style={{ fontWeight: 800, color: '#92400e', fontSize: '0.88rem' }}>Old Scrap Melting Status</div>
              <div style={{ fontSize: '0.78rem', color: '#b45309' }}>28.5g of 18KT/22KT scrap queued for refinery recycling into 999 24KT fine bars.</div>
            </div>
          </div>
          <p style={{ fontSize: '0.82rem', color: '#64748b', margin: 0 }}>
            All melting batches are logged with pre-melt weight, post-melt bar weight, touch testing percentage, and refinery conversion vouchers.
          </p>
        </div>
      )}

      {/* Barcode Print Modal */}
      {selectedBarcodeItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}>
          <div style={{ background: '#ffffff', borderRadius: '8px', maxWidth: '420px', width: '100%', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
            <div style={{ background: '#212121', color: '#fff', padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 800, fontSize: '0.95rem' }}>Jewellery Barcode Tag (Jewellery Tag 50x12mm)</span>
              <button onClick={() => setSelectedBarcodeItem(null)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: '1.5rem', textAlign: 'center' }}>
              <div style={{ border: '2px dashed #94a3b8', padding: '1.2rem', borderRadius: '6px', background: '#fafafa', display: 'inline-block', width: '100%' }}>
                <div style={{ fontWeight: 900, fontSize: '0.95rem', color: '#1e293b', letterSpacing: '-0.3px' }}>M.L. JEWELLERS JAIPUR</div>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#2563eb', margin: '0.2rem 0' }}>{selectedBarcodeItem.name}</div>
                <div style={{ fontSize: '0.72rem', color: '#475569', fontWeight: 600 }}>
                  GW: {selectedBarcodeItem.wt.toFixed(2)}g | NW: {(selectedBarcodeItem.wt * 0.96).toFixed(2)}g | {selectedBarcodeItem.purity || '22K'}
                </div>
                
                {/* Visual Barcode Graphic */}
                <div style={{ margin: '0.8rem auto 0.4rem', height: '36px', background: 'repeating-linear-gradient(90deg, #000 0, #000 2px, transparent 2px, transparent 4px, #000 4px, #000 7px, transparent 7px, transparent 9px)', width: '85%' }} />
                
                <div style={{ fontSize: '0.8rem', fontWeight: 800, fontFamily: 'monospace', color: '#000' }}>
                  {selectedBarcodeItem.sku}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#b45309', fontWeight: 800, marginTop: '0.2rem' }}>
                  BIS HUID: {selectedBarcodeItem.huid}
                </div>
              </div>

              <div style={{ marginTop: '1.2rem', display: 'flex', gap: '0.6rem' }}>
                <button
                  onClick={() => {
                    window.print();
                    addToast?.('Barcode sent to thermal label printer', 'success');
                  }}
                  style={{
                    flex: 1,
                    padding: '0.65rem',
                    borderRadius: '4px',
                    border: 'none',
                    background: '#2874f0',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    cursor: 'pointer'
                  }}
                >
                  Print Label Tag
                </button>
                <button
                  onClick={() => setSelectedBarcodeItem(null)}
                  style={{
                    padding: '0.65rem 1rem',
                    borderRadius: '4px',
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    color: '#334155',
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stock In Purchase Entry Modal */}
      {isStockInModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}>
          <form onSubmit={handleCreateStockIn} style={{ background: '#ffffff', borderRadius: '8px', maxWidth: '540px', width: '100%', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
            <div style={{ background: '#2874f0', color: '#fff', padding: '1rem 1.4rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800 }}>Inward Purchase Voucher (Stock In)</h3>
              <button type="button" onClick={() => setIsStockInModalOpen(false)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: '1.3rem', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Supplier / Karigar Workshop *</label>
                <input
                  type="text"
                  required
                  value={stockInForm.supplierName}
                  onChange={(e) => setStockInForm({ ...stockInForm, supplierName: e.target.value })}
                  style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Metal Type</label>
                  <select
                    value={stockInForm.metalType}
                    onChange={(e) => setStockInForm({ ...stockInForm, metalType: e.target.value })}
                    style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem' }}
                  >
                    <option value="Gold">Gold</option>
                    <option value="Silver">Silver</option>
                    <option value="Platinum">Platinum</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Carat Purity</label>
                  <select
                    value={stockInForm.purity}
                    onChange={(e) => setStockInForm({ ...stockInForm, purity: e.target.value })}
                    style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem' }}
                  >
                    <option value="22KT (916)">22KT (916)</option>
                    <option value="24KT (999)">24KT (999)</option>
                    <option value="18KT (750)">18KT (750)</option>
                    <option value="999 Pure Silver">999 Pure Silver</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.8rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Gross Wt (g)</label>
                  <input
                    type="number"
                    step="0.001"
                    required
                    value={stockInForm.grossWeight}
                    onChange={(e) => setStockInForm({ ...stockInForm, grossWeight: parseFloat(e.target.value) || 0 })}
                    style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Net Wt (g)</label>
                  <input
                    type="number"
                    step="0.001"
                    required
                    value={stockInForm.netWeight}
                    onChange={(e) => setStockInForm({ ...stockInForm, netWeight: parseFloat(e.target.value) || 0 })}
                    style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Quantity</label>
                  <input
                    type="number"
                    required
                    value={stockInForm.quantity}
                    onChange={(e) => setStockInForm({ ...stockInForm, quantity: parseInt(e.target.value, 10) || 1 })}
                    style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '0.8rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>BIS HUID (6-Character)</label>
                  <input
                    type="text"
                    maxLength={6}
                    placeholder="e.g. AB1234"
                    value={stockInForm.huid}
                    onChange={(e) => setStockInForm({ ...stockInForm, huid: e.target.value.toUpperCase() })}
                    style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem', textTransform: 'uppercase', fontWeight: 700 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Target Vault Branch</label>
                  <select
                    value={stockInForm.branch}
                    onChange={(e) => setStockInForm({ ...stockInForm, branch: e.target.value })}
                    style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem' }}
                  >
                    <option value="b1">Main Showroom</option>
                    <option value="b2">Bapu Bazar</option>
                    <option value="b3">E-Commerce Safe</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.8rem' }}>
                <button
                  type="submit"
                  style={{ flex: 1, padding: '0.65rem', borderRadius: '4px', border: 'none', background: '#2874f0', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  Record Inward Stock
                </button>
                <button
                  type="button"
                  onClick={() => setIsStockInModalOpen(false)}
                  style={{ padding: '0.65rem 1rem', borderRadius: '4px', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

    </div>
  );
}
